import { Download } from 'lucide-react'
import { toast } from 'sonner'

import { PurchaseRecord, purchasesService } from '../../../services/purchases.service'
import { Badge } from '../../ui/badge'
import { Button } from '../../ui/button'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../../ui/dialog'
import { DiscreteLoader } from '../../ui/discrete-loader'

interface DetailModalProps {
  showDetailModal: boolean
  setShowDetailModal: (show: boolean) => void
  selectedEntry: PurchaseRecord | null
  isLoading?: boolean
}

const money = (value: string | null | undefined, currency = 'MXN') => value == null
  ? 'Sin capturar'
  : new Intl.NumberFormat('es-MX', { style: 'currency', currency }).format(Number(value))

export function DetailModal({ showDetailModal, setShowDetailModal, selectedEntry, isLoading = false }: DetailModalProps) {
  const downloadDocument = async () => {
    if (!selectedEntry) return
    try {
      const blob = await purchasesService.downloadPurchaseDocument(selectedEntry.id)
      const url = URL.createObjectURL(blob)
      const anchor = document.createElement('a')
      anchor.href = url
      anchor.download = selectedEntry.document_name || `compra-${selectedEntry.id}.xml`
      anchor.click()
      URL.revokeObjectURL(url)
    } catch {
      toast.error('No fue posible descargar el documento')
    }
  }
  return (
    <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
      <DialogContent className="flex max-h-[90dvh] w-[calc(100vw-2rem)] min-w-0 flex-col gap-0 overflow-hidden p-0 sm:max-w-7xl">
        <DialogHeader className="shrink-0 border-b border-slate-200 px-5 py-4 pr-14 sm:px-6">
          <DialogTitle>Detalle de Ingreso</DialogTitle>
          <DialogDescription>Información de la nota y productos recibidos.</DialogDescription>
        </DialogHeader>

        <div data-slot="purchase-detail-body" className="min-h-0 min-w-0 flex-1 overflow-x-hidden overflow-y-auto p-4 sm:p-6">
          {isLoading ? (
            <div className="min-h-52"><DiscreteLoader text="Cargando detalle..." /></div>
          ) : selectedEntry ? (
            <div className="min-w-0 space-y-5">
              <dl className="grid min-w-0 grid-cols-1 gap-x-4 gap-y-5 rounded-xl border border-[var(--layout-border)] bg-[var(--layout-bg)] p-5 sm:grid-cols-2 lg:grid-cols-4 [&>div]:flex [&>div]:min-w-0 [&>div]:flex-col [&>div]:gap-0.5 [&_dd]:break-words [&_dd]:text-sm [&_dd]:font-medium [&_dd]:text-[var(--layout-text-primary)] [&_dt]:text-xs [&_dt]:text-[var(--layout-text-secondary)]">
                <div><dt>Folio</dt><dd>{selectedEntry.reference_number}</dd></div>
                <div><dt>Origen</dt><dd><Badge variant="outline">{selectedEntry.source === 'cfdi_xml' ? 'CFDI XML' : selectedEntry.source === 'excel' ? 'Excel' : 'Manual / Sin XML'}</Badge></dd></div>
                <div><dt>Sucursal</dt><dd>{selectedEntry.sucursal.name}</dd></div>
                <div><dt>Proveedor</dt><dd>{selectedEntry.supplier.name}</dd></div>
                <div><dt>Estado</dt><dd><Badge className="bg-blue-100 text-blue-700 hover:bg-green-100">Procesado</Badge></dd></div>
                <div><dt>Fecha de compra</dt><dd>{selectedEntry.purchase_date}</dd></div>
                <div><dt>Fecha de ingreso</dt><dd>{selectedEntry.entry_date}</dd></div>
                <div><dt>Forma de pago</dt><dd>{selectedEntry.payment_condition === 'cash' ? 'Contado' : 'Crédito'}</dd></div>
                <div><dt>Método de pago</dt><dd>{selectedEntry.payment_method.name}</dd></div>
                <div><dt>Subtotal</dt><dd>{money(selectedEntry.subtotal_amount, selectedEntry.currency)}</dd></div>
                <div><dt>Impuestos</dt><dd>{money(selectedEntry.tax_total_amount, selectedEntry.currency)}</dd></div>
                {selectedEntry.source === 'cfdi_xml' && <div><dt>Descuentos</dt><dd>{money(selectedEntry.discount_total_amount, selectedEntry.currency)}</dd></div>}
                {selectedEntry.source === 'cfdi_xml' && <div><dt>Retenciones</dt><dd>{money(selectedEntry.withheld_tax_total_amount, selectedEntry.currency)}</dd></div>}
                <div><dt>Total</dt><dd className="!font-semibold !text-blue-600">{money(selectedEntry.total_amount, selectedEntry.currency)}</dd></div>
                <div><dt>Revisión de precios</dt><dd>{selectedEntry.price_review_status === 'completed' ? 'Completa' : 'Pendiente'}</dd></div>
                {selectedEntry.document_name && <div className="sm:col-span-2 lg:col-span-3"><dt>Archivo</dt><dd className="truncate" title={selectedEntry.document_name}>{selectedEntry.document_name}</dd></div>}
              </dl>

              {selectedEntry.source === 'cfdi_xml' && (
                <div className="min-w-0 space-y-3 rounded-xl border border-border bg-card p-5 text-card-foreground">
                  <div className="flex min-w-0 flex-wrap items-center justify-between gap-3">
                    <div className="min-w-0"><p className="font-medium text-card-foreground">Información fiscal CFDI {selectedEntry.cfdi_version}</p><p className="break-all text-sm text-primary">UUID: {selectedEntry.cfdi_uuid}</p></div>
                    <Button type="button" variant="outline" onClick={() => void downloadDocument()}><Download className="mr-2 h-4 w-4" />Descargar XML</Button>
                  </div>
                  <div className="grid min-w-0 gap-3 text-sm md:grid-cols-2 [&>p]:min-w-0 [&>p]:break-words"><p><span className="text-card-description">Emisor:</span> {selectedEntry.issuer_name} · {selectedEntry.issuer_rfc}</p><p><span className="text-card-description">Receptor:</span> {selectedEntry.receiver_name} · {selectedEntry.receiver_rfc}</p><p><span className="text-card-description">Pago SAT:</span> {selectedEntry.sat_payment_method_code || '—'} / {selectedEntry.sat_payment_form_code || '—'}</p></div>
                </div>
              )}

              {(selectedEntry.taxes?.length ?? 0) > 0 && (
                <div className="min-w-0 space-y-3 rounded-xl border border-amber-200 bg-amber-50/50 p-5">
                  <div>
                    <p className="font-medium text-amber-950">Impuestos locales del comprobante</p>
                    <p className="text-sm text-amber-800">Estos importes pertenecen al CFDI completo y no se asignan a una partida.</p>
                  </div>
                  <div className="grid gap-2 sm:grid-cols-2">
                    {selectedEntry.taxes?.map((tax) => (
                      <div key={tax.id} className="min-w-0 rounded-md border border-amber-200 bg-white p-3 text-sm">
                        <p className="break-words font-medium">{tax.tax_name}</p>
                        <p className="text-slate-600">
                          {tax.tax_kind === 'transfer' ? 'Traslado' : 'Retención'}
                          {tax.tax_rate !== null ? ` · ${Number(tax.tax_rate).toLocaleString('es-MX')}%` : ''}
                        </p>
                        <p className="font-semibold text-slate-900">{money(tax.tax_amount, selectedEntry.currency)}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div data-slot="purchase-items-scroll" className="min-w-0 max-w-full overflow-x-auto overscroll-x-contain rounded-lg border border-border bg-table-bg">
                <table className="w-full text-sm">
                <thead className="border-b border-border bg-table-header-bg text-left text-[11px] font-semibold uppercase tracking-wider text-table-header-foreground">
                  <tr>
                    <th className="px-3 py-4">Producto</th>
                    
                    <th className="px-3 py-4 text-center">Cantidad</th>
                    <th className="px-3 py-4 text-right">Costo<br/>Anterior</th>
                    <th className="px-3 py-4 text-right">Precio Base<br/>Anterior</th>
                    <th className="px-3 py-4 text-right">Precio Venta<br/>Anterior</th>
                    <th className="px-3 py-4 text-right">Utilidad<br/>Anterior</th>
                    <th className="px-3 py-4 text-right">Costo CFDI/<br/>Recibido</th>
                    <th className="px-3 py-4">Lote</th>
                    <th className="px-3 py-4">Estado</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border text-table-foreground">
                  {(selectedEntry.items ?? []).map((item) => (
                    <tr key={item.id} className="transition-colors hover:bg-table-row-hover">
                      <td className="px-3 py-4"><p className="font-medium text-table-foreground">{item.original_name}</p>{item.original_barcode && (<p className="mt-1 font-mono text-xs text-card-description">{item.original_barcode}</p>)}<p className="text-xs text-card-description">SAT {item.sat_product_key_code || 'Sin capturar'}</p></td>
                      <td className="px-3 py-4 text-center"><span className="font-medium text-table-foreground">{item.quantity ?? '—'}</span></td>
                      <td className="px-3 py-4 text-right"><span className="text-card-description">{money(item.previous_cost_amount, selectedEntry.currency)}</span></td>
                      <td className="px-3 py-4 text-right"><span className="text-card-description">{money(item.previous_base_price_amount, selectedEntry.currency)}</span></td>
                      <td className="px-3 py-4 text-right"><span className="text-card-description">{money(item.previous_sale_price_amount, selectedEntry.currency)}</span></td>
                      <td className="px-3 py-4 text-right"><span className="text-card-description">{item.previous_profit_percentage === null ? '—' : `${item.previous_profit_percentage}%`}</span></td>
                      <td className="px-3 py-4 text-right"><span className="font-semibold text-table-foreground">{money(item.unit_cost_amount, selectedEntry.currency)}</span></td>
                      <td className="px-3 py-4"><span className="text-xs text-card-description">{item.lot_number ? `${item.lot_number}${item.expiration_date ? ` · ${item.expiration_date}` : ''}` : 'Sin lote'}</span></td>
                      <td className="px-3 py-4">
                        <Badge className={item.price_decision === 'applied' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}>
                          {item.match_status === 'non_inventory' ? 'No inventariable' : item.match_status === 'existing_product' ? 'Conciliado' : item.price_decision === 'applied' ? 'Aplicado' : 'Pendiente'}
                        </Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
                </table>
              </div>
            </div>
          ) : (
            <p className="py-8 text-center text-sm text-card-description">No fue posible cargar el detalle.</p>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}
